import { Box, Chip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * The header every admin page starts with: a title, one line saying what the
 * page is for, and a badge saying who can change what is on it. Keeping the
 * badge here means the audience is always stated the same way.
 */

export type AdminAudience = 'admins' | 'partners' | 'fisci';

const AUDIENCE: Record<AdminAudience, { label: string; sx: object }> = {
  admins: { label: 'Administrators', sx: { bgcolor: '#e9eef3', color: '#3b4a5a' } },
  partners: { label: 'Partners & FiSci', sx: { bgcolor: '#fff1d6', color: '#7a4d00' } },
  fisci: { label: 'FiSci staff only', sx: { bgcolor: '#7b5cff', color: '#fff' } },
};

interface Props {
  title: string;
  description: string;
  audience?: AdminAudience;
  /** Right-aligned action, e.g. a primary button. */
  action?: ReactNode;
}

export default function AdminPageHeader({ title, description, audience = 'admins', action }: Props) {
  const badge = AUDIENCE[audience];
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        {action}
        <Chip size="small" label={badge.label} sx={{ fontWeight: 500, ...badge.sx }} />
      </Box>
    </Box>
  );
}
